import type { SessionState, User } from '@liniya/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api, onAuthenticationLost } from './api';
import { queryKeys } from './query-keys';

interface SessionContextValue {
  user: User | null;
  deviceId: string | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue>({
  user: null,
  deviceId: null,
  status: 'loading',
  signOut: async () => undefined,
  refresh: async () => undefined,
});

/**
 * Establishes who is signed in, once, for the whole application.
 *
 * On a fresh page load there is no access token — it was never written to disk
 * — so the first thing that happens is an attempt to trade the refresh
 * credential for a new one. Until that answer arrives the status is `loading`,
 * and routes wait rather than bouncing the user to the sign-in screen and back.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [resumed, setResumed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api.auth.resume().finally(() => {
      if (!cancelled) setResumed(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const query = useQuery<SessionState>({
    queryKey: queryKeys.session,
    queryFn: async () => {
      const tokens = await api.http.currentTokens();
      if (!tokens) return { authenticated: false };
      return await api.auth.session();
    },
    enabled: resumed,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(
    () =>
      onAuthenticationLost(() => {
        queryClient.setQueryData<SessionState>(queryKeys.session, { authenticated: false });
        // Everything cached belonged to the session that just ended.
        queryClient.removeQueries({ predicate: (item) => item.queryKey[0] !== 'session' });
      }),
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await api.auth.signOut();
    // Media URLs carry a ticket bound to the account that minted it. Leaving
    // them cached would hand the next person to sign in on this tab a set of
    // links that only refuse to load.
    api.media.clearDisplayUrls();
    queryClient.setQueryData<SessionState>(queryKeys.session, { authenticated: false });
    queryClient.removeQueries({ predicate: (item) => item.queryKey[0] !== 'session' });
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
  }, [queryClient]);

  const value = useMemo<SessionContextValue>(() => {
    const status: SessionContextValue['status'] = !resumed || query.isLoading
      ? 'loading'
      : query.data?.authenticated
        ? 'authenticated'
        : 'anonymous';
    return {
      user: query.data?.user ?? null,
      deviceId: query.data?.device_id ?? null,
      status,
      signOut,
      refresh,
    };
  }, [query.data, query.isLoading, refresh, resumed, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}

/** The signed-in account, for code that only runs behind an authenticated route. */
export function useViewer(): User {
  const { user } = useSession();
  if (!user) {
    throw new Error('useViewer вызван вне авторизованной части приложения.');
  }
  return user;
}
